import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Calendar, Tag, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ImageZoomViewer } from './ImageZoomViewer';
import { logger } from '../utils/logger';
import { ANIMAL_ICONS } from '../theme';

import { BACKEND_URL } from '../utils/api';

const CATEGORY_COLORS = {
  fashion: 'from-[#D4AF37] to-[#B8926A]',
  food: 'from-[#D4AF37]/80 to-[#B8926A]',
  explore: 'from-[#0F52BA] to-[#3B82F6]',
  souls: 'from-[#D4AF37]/60 to-[#B8926A]/60',
  sporty: 'from-[#10B981] to-[#059669]',
  success: 'from-[#D4AF37] to-[#E5C44D]',
  legacy: 'from-[#D4AF37]/70 to-[#B8926A]/70',
};

const getCategoryColor = (category) => CATEGORY_COLORS[category] || 'from-[#D4AF37] to-[#B8926A]';

// Small animal icon badge — bottom-right splash, never blocks image/text
const AnimalBadge = ({ category }) => {
  const icon = ANIMAL_ICONS[category];
  if (!icon) return null;
  return (
    <div className="relative" data-testid={`animal-badge-${category}`}>
      {/* Gold flash ring splash */}
      <motion.div
        className="absolute inset-0 rounded-full"
        initial={{ scale: 1, opacity: 0.7 }}
        animate={{ scale: 2.5, opacity: 0 }}
        transition={{ duration: 0.9, ease: 'easeOut', repeat: Infinity, repeatDelay: 1.5 }}
        style={{ border: '1.5px solid #D4AF37' }}
      />
      <motion.div
        className="w-7 h-7 rounded-full overflow-hidden border border-[#D4AF37]/70"
        style={{ boxShadow: '0 0 8px rgba(212,175,55,0.5), 0 0 16px rgba(212,175,55,0.2)' }}
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 350, damping: 12 }}
      >
        <img src={icon.src} alt={category} className="w-full h-full object-cover" style={{ objectPosition: icon.pos }} />
      </motion.div>
    </div>
  );
};

// Exportable for reuse in UserProfile
export { AnimalBadge };

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const MediaThumbnail = ({ memory }) => {
  if (memory.media_type === 'video') {
    return <video src={memory.image_url} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" muted />;
  }
  if (memory.media_type === 'audio') {
    return (
      <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/10 to-[#05050A] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full bg-[#D4AF37]/30 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
        </div>
      </div>
    );
  }
  return <img src={memory.image_url} alt={memory.note} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />;
};

const MemoryGridItem = ({ memory, index, selectMode, isSelected, onClick, onLongPress }) => {
  let longPressTimer = null;
  return (
    <motion.div
      key={memory.id || index}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, delay: Math.min(index * 0.01, 0.15) }}
      className={`relative aspect-square cursor-pointer group overflow-hidden rounded-lg border border-[#D4AF37]/10 ${isSelected ? 'ring-3 ring-[#D4AF37] ring-offset-1 ring-offset-black' : ''}`}
      onClick={() => onClick(memory, index)}
      onTouchStart={() => { longPressTimer = setTimeout(() => onLongPress(memory), 500); }}
      onTouchEnd={() => clearTimeout(longPressTimer)}
      onTouchMove={() => clearTimeout(longPressTimer)}
      onMouseDown={() => { longPressTimer = setTimeout(() => onLongPress(memory), 500); }}
      onMouseUp={() => clearTimeout(longPressTimer)}
      onMouseLeave={() => clearTimeout(longPressTimer)}
      data-testid={`memory-grid-item-${index}`}
    >
      <MediaThumbnail memory={memory} />
      {selectMode && (
        <div className={`absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center transition-all z-10 ${isSelected ? 'bg-[#D4AF37] shadow-lg' : 'bg-black/30 border-2 border-white/30'}`}>
          {isSelected && <CheckCircle2 size={16} className="text-white" />}
        </div>
      )}
      {selectMode && isSelected && <div className="absolute inset-0 bg-[#D4AF37]/15" />}
      {!selectMode && (
        <div className="absolute bottom-1.5 right-1.5 z-[5]">
          <AnimalBadge category={memory.category} />
        </div>
      )}
      {!selectMode && (
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="text-white text-center px-2">
            <p className="font-['Outfit'] text-xs line-clamp-2">{memory.note}</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export const AllMemoriesGrid = () => {
  const [allMemories, setAllMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();

  const loadAllMemories = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/memories`
      );
      setAllMemories(data);
    } catch (error) {
      logger.error('Failed to load memories:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllMemories();
  }, [loadAllMemories]);

  const handleMemoryClick = (memory, index) => {
    setSelectedMemory(memory);
    setSelectedIndex(index);
  };

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      setSelectedMemory(allMemories[selectedIndex - 1]);
    }
  };

  const handleNext = () => {
    if (selectedIndex < allMemories.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      setSelectedMemory(allMemories[selectedIndex + 1]);
    }
  };

  const handleDelete = async (memoryId) => {
    if (!window.confirm('Are you sure you want to delete this vibe?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/memories/${memoryId}`);
      const updatedMemories = allMemories.filter((m) => m.id !== memoryId);
      setAllMemories(updatedMemories);
      setSelectedMemory(null);
    } catch (error) {
      logger.error('Failed to delete memory:', error);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === allMemories.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allMemories.map((m) => m.id)));
    }
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(`Delete ${count} vibe${count > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await Promise.all(
        [...selectedIds].map((id) =>
          axios.delete(`${BACKEND_URL}/api/memories/${id}`)
        )
      );
      setAllMemories((prev) => prev.filter((m) => !selectedIds.has(m.id)));
      exitSelectMode();
    } catch (error) {
      logger.error('Failed to bulk delete memories:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleItemClick = (memory, index) => {
    if (selectMode) {
      toggleSelect(memory.id);
    } else {
      handleMemoryClick(memory, index);
    }
  };

  const handleItemLongPress = (memory) => {
    if (!selectMode) {
      setSelectMode(true);
      setSelectedIds(new Set([memory.id]));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="pb-4" data-testid="all-memories-grid">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#D4AF37]/10 sticky top-0 z-10" style={{ background: 'rgba(5,5,10,0.9)', backdropFilter: 'blur(12px)' }}>
        {selectMode ? (
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button onClick={exitSelectMode} className="text-white/30 hover:text-white" data-testid="exit-select-mode">
                <XCircle size={22} />
              </button>
              <span className="font-['Cormorant_Garamond'] text-lg font-light text-white">
                {selectedIds.size} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={selectAll}
                className="px-3 py-1.5 rounded-full font-['Outfit'] text-xs font-semibold border border-white/10 text-white/50 hover:border-[#D4AF37]/30 transition-colors"
                data-testid="select-all-button">
                {selectedIds.size === allMemories.length ? 'Deselect All' : 'Select All'}
              </button>
              <button onClick={handleBulkDelete} disabled={selectedIds.size === 0 || deleting}
                className="px-4 py-1.5 rounded-full font-['Outfit'] text-xs font-semibold bg-red-500/80 text-white hover:bg-red-500 transition-colors disabled:opacity-40 flex items-center gap-1.5"
                data-testid="bulk-delete-button">
                <Trash2 size={14} />
                {deleting ? 'Deleting...' : `Delete (${selectedIds.size})`}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-white">All Vibes</h2>
              <p className="font-['Outfit'] text-sm text-white/30">
                {allMemories.length} {allMemories.length === 1 ? 'vibe' : 'vibes'}
              </p>
            </div>
            {allMemories.length > 0 && (
              <button onClick={() => setSelectMode(true)}
                className="px-4 py-2 rounded-full font-['Outfit'] text-xs font-semibold border border-white/10 text-white/40 hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                data-testid="enter-select-mode">
                Select
              </button>
            )}
          </div>
        )}
      </div>

      {allMemories.length === 0 ? (
        <div className="text-center py-20">
          <p className="font-['Outfit'] text-white/30 mb-4">No vibes yet</p>
          <p className="font-['Outfit'] text-sm text-white/20">
            Start creating vibes in your favorite categories!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-1 p-1">
          {allMemories.map((memory, index) => (
            <MemoryGridItem
              key={memory.id || index}
              memory={memory}
              index={index}
              selectMode={selectMode}
              isSelected={selectedIds.has(memory.id)}
              onClick={handleItemClick}
              onLongPress={handleItemLongPress}
            />
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedMemory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
            onClick={() => setSelectedMemory(null)}
            data-testid="memory-lightbox"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-6xl w-full h-full flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setSelectedMemory(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="close-lightbox"
              >
                <X size={24} className="text-white" />
              </button>

              {/* Previous button */}
              {selectedIndex > 0 && (
                <button
                  onClick={handlePrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                  data-testid="previous-memory"
                >
                  <ChevronLeft size={28} className="text-white" />
                </button>
              )}

              {/* Next button */}
              {selectedIndex < allMemories.length - 1 && (
                <button
                  onClick={handleNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                  data-testid="next-memory"
                >
                  <ChevronRight size={28} className="text-white" />
                </button>
              )}

              {/* Image and details */}
              <div className="flex flex-col lg:flex-row gap-4 max-h-[90vh] w-full">
                {/* Image with zoom */}
                <div className="flex-1 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                  <ImageZoomViewer
                    src={selectedMemory.image_url}
                    alt={selectedMemory.note}
                    className="flex items-center justify-center w-full"
                  />
                </div>

                {/* Details sidebar */}
                <div className="lg:w-80 rounded-lg p-6 overflow-y-auto" style={{ background: 'linear-gradient(180deg, #0c0c14 0%, #05050A 100%)' }}>
                  {/* Category animal icon badge */}
                  <div className="flex items-center gap-3 mb-4">
                    {ANIMAL_ICONS[selectedMemory.category] && (
                      <div className="w-10 h-10 rounded-full overflow-hidden border border-[#D4AF37]/50" style={{ boxShadow: '0 0 12px rgba(212,175,55,0.3)' }}>
                        <img src={ANIMAL_ICONS[selectedMemory.category].src} alt={selectedMemory.category} className="w-full h-full object-cover" style={{ objectPosition: ANIMAL_ICONS[selectedMemory.category].pos }} />
                      </div>
                    )}
                    <span className="font-['Cormorant_Garamond'] text-lg text-[#D4AF37] capitalize tracking-wide">{selectedMemory.category}</span>
                  </div>

                  {/* Note */}
                  <div className="mb-4">
                    <h3 className="font-['Cormorant_Garamond'] text-xl font-light text-white mb-2">
                      Vibe Note
                    </h3>
                    <p className="font-['Outfit'] text-white/70 leading-relaxed">
                      {selectedMemory.note}
                    </p>
                  </div>

                  {/* Metadata */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-white/30">
                      <Calendar size={16} />
                      <span className="font-['Outfit'] text-sm">
                        {formatDate(selectedMemory.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-white/30">
                      <Tag size={16} />
                      <span className="font-['Outfit'] text-sm">
                        Memory #{selectedIndex + 1} of {allMemories.length}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-4 border-t border-white/5">
                    <button
                      onClick={() => handleDelete(selectedMemory.id)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors font-['Outfit'] font-medium"
                      data-testid="delete-memory-lightbox"
                    >
                      <Trash2 size={18} />
                      Delete Vibe
                    </button>
                  </div>
                </div>
              </div>

              {/* Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="font-['Outfit'] text-white text-sm">
                  {selectedIndex + 1} / {allMemories.length}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
